import {
  Component,
  Injector,
  OnInit,
  effect,
  inject,
  runInInjectionContext,
} from '@angular/core';
import { SharedModule } from 'src/app/shared.module';
import { CtaButtonsComponent } from 'src/app/components/cta-buttons/cta-buttons.component';
import { starterPages } from '../starter-data';
import { ReactiveFileInputComponent } from 'src/app/components/inputs/reactive-file-input/reactive-file-input.component';
import {
  DocumentUploadForm,
  DocumentUploadFormModel,
} from 'src/app/components/document-upload/models/document-upload-form.model';
import { ValidationMessageFn } from 'src/app/components/inputs/types/validation-message-fn';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, Validators, FormControl, FormGroup } from '@angular/forms';
import { UploadService } from 'src/app/services/upload.service';
import { AuthService } from 'src/app/auth.service';
import { v4 as uuidv4 } from 'uuid';
import i18next from 'i18next';
import { I18NextEagerPipe } from 'angular-i18next';
import { bankOptions, BankOption } from '../starter-data';
import { StarterFormDataService } from '../starter-form-data.service';
import { NavigationEnd, Router } from '@angular/router';
import { ReactiveTextInputComponent } from 'src/app/components/inputs/reactive-text-input/reactive-text-input.component';
import { ReactiveNgSelectFieldComponent } from 'src/app/components/reactive-ng-select-field/reactive-ng-select-field.component';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'app-account-details-th',
  standalone: true,
  imports: [
    SharedModule,
    CtaButtonsComponent,
    ReactiveFileInputComponent,
    CommonModule,
    ReactiveFormsModule,
    ReactiveTextInputComponent,
    ReactiveNgSelectFieldComponent,
  ],
  templateUrl: './account-details-th.component.html',
  styleUrl: './account-details-th.component.scss',
})
export class AccountDetailsThComponent implements OnInit {
  public starterPages = starterPages;
  public formData: any = {
    bankName: null,
    accountName: null,
    accountNumber: null,
  };
  bankOptions: BankOption[] = bankOptions;
  bankNames: string[] = [];
  bankName: FormControl = new FormControl(null, Validators.required);
  accountName: FormControl = new FormControl();
  accountNumber: FormControl = new FormControl();
  minNum: number = 10;
  maxNum: number = 12;
  isAllFilled = false;
  formGroup: FormGroup;
  public form: DocumentUploadForm = DocumentUploadFormModel.build({
    expiration: {
      validators: [],
    },
    file: {
      validators: [Validators.required],
    },
    idType: {
      validators: [Validators.required],
    },
  });
  public capturecam: string | null = null;
  private injector = inject(Injector);
  private fileSizeLimit: number = 2 * 1024 * 1024;
  public isLoading: boolean = false;
  public isNextDisabled: boolean = false;
  public uploadError: string = '';
  public merchantId: number | null = null;

  constructor(
    private uploadService: UploadService,
    private authService: AuthService,
    private starterService: StarterFormDataService,
    router: Router
  ) {
    router.events.subscribe((val) => {
      if (val instanceof NavigationEnd) {
        console.log('navigated to ', val);
        this.fetchMerchantData();
      }
    });

    this.formGroup = new FormGroup({
      bankName: this.bankName,
      accountName: this.accountName,
      accountNumber: this.accountNumber,
    });

    this.bankName.valueChanges.subscribe((value) => {
      this.formData.bankName = value;
      this.updateBankDetails(value);
      this.checkRequiredFields();
    });

    this.accountName.valueChanges.subscribe(() => {
      this.formData.accountName = this.accountName.value;
      this.checkRequiredFields();
    });

    this.accountNumber.valueChanges.subscribe(() => {
      this.formData.accountNumber = this.accountNumber.value;
      this.checkRequiredFields();
    });
  }

  handleNext = async () => {
    this.isLoading = true;

    var merchantData = this.starterService.merchantData;
    merchantData.bank_details.bank_name = this.bankName.value;
    merchantData.bank_details.account_number = this.accountNumber.value;
    merchantData.bank_details.account_name = this.accountName.value;
    merchantData.account_number = this.accountNumber.value;
    merchantData.bank_name = this.bankName.value;
    merchantData.account_name = this.accountName.value;
    console.log("bank account", merchantData)
    await lastValueFrom(this.starterService.updateBankAccountDetails(merchantData));
    await lastValueFrom(this.starterService.updateMerchantDetails(merchantData));

    return await this.submitBankBook().then((bankBookResults: any) => {
      return bankBookResults;
    });

    return true;
  };

  private readonly i18nextPipe = inject(I18NextEagerPipe);

  public get validationMessages(): Record<string, ValidationMessageFn> {
    return {
      required: () => this.i18nextPipe.transform('general_validation.required') + ' *',
      fileInvalid: () => this.i18nextPipe.transform('general_validation.file-invalid'),
    };
  }

  ngOnInit(): void {
    this.isNextDisabled = true;
    this.merchantId = parseInt(localStorage.getItem('userId') ?? '');
    this.starterService.setCheckPointIndex(3);
    this.bankNames = this.bankOptions.map((bank) => bank.name);
    this.fetchMerchantData();
    runInInjectionContext(this.injector, () => {
      const formValue = toSignal(this.form.valueChanges);

      effect(() => {
        const data = formValue();
        if (!data) return;

        this.isNextDisabled = false;

        console.log(this.isNextDisabled, this.isLoading);
      });
    });
  }

  async submitBankBook(): Promise<boolean> {
    if (!this.merchantId) {
      this.uploadError =
        'Something went wrong with the upload, please try again';
      console.log('No merchant ID on primary ID upload');
      this.isLoading = false;
      return false;
    }

    this.form.markAllAsTouched();
    this.form.controls['expiration'].setValue(null);
    this.form.controls['idType'].setValue('bank-book');

    if (!this.form.valid) {
      this.isLoading = false;
      return false;
    }

    const file = this.form.value.file;

    if (!file) {
      this.uploadError = 'A file is required before proceeding';
      this.isLoading = false;
      return false;
    }

    const validatedBankBook = await this.validateAndHandleLargeFile(
      file,
      'bank-book'
    );

    const payload = {
      file: validatedBankBook,
      merchant_id: this.merchantId,
      uid: uuidv4(),
      document_name: 'bank-book',
      extras: JSON.stringify({
        id_subtype: null,
        expiration_date: null,
      }),
    };

    console.log('Bank Book Payload', payload, this.merchantId);

    return await this.uploadFile(payload)
      .then()
      .catch((error) => {
        return this.handleUploadError(error, file);
      });
  }

  async validateAndHandleLargeFile(
    file: File,
    idType: string
  ): Promise<File | null> {
    if (file.size > this.fileSizeLimit) {
      console.log('IC00 Trigger for image compression', file.size, idType);
      const compressedFile = await this.uploadService.compressImage(file);
      if (!compressedFile) {
        this.isLoading = false;
        return null;
      }
      return compressedFile;
    }
    return file;
  }

  handleUploadError(error: Error, file: File | null | undefined): boolean {
    if (file instanceof File) {
      this.uploadError = i18next.t('starter-pages.bank-details.upload-error');
      console.error('Error uploading primary ID: ', error, file.size);
      this.isLoading = false;
      return false;
    } else {
      console.error('File is not valid:', file);
      this.isLoading = false;
      return false;
    }
  }

  uploadFile(payload: any): Promise<any> {
    return this.uploadService
      .uploadFile(
        payload.file,
        payload.merchant_id,
        payload.uid,
        payload.document_name,
        payload.status,
        payload.extras
      )
      .toPromise();
  }

  checkRequiredFields() {
    this.isAllFilled =
      this.bankName.valid &&
      this.bankName.touched &&
      this.accountName.valid &&
      this.accountNumber.valid;
  }

  fetchMerchantData() {
    if (!this.starterService.merchantData) {
      this.starterService.getMerchantDetailsV2().subscribe((data: any) => {
        this.starterService.merchantData = data;
        this.populateForm();
      });
    } else {
      this.populateForm();
    }
  }

  populateForm() {
    const merchantData = this.starterService.merchantData;
    if (!merchantData) return;

    const fullName = `${merchantData.first_name} ${merchantData.last_name}`;

    this.bankName.setValue(
      this.starterService.merchantData.bank_details?.bank_name || ''
    );

    this.accountName.setValue(fullName);

    this.accountNumber.setValue(
      this.starterService.merchantData.bank_details.account_number
    );
  }

  checkIfFormIsValid() {
    this.isAllFilled = this.formGroup.valid;
  }

  public getFieldError(field: string) {
    return this.formGroup.get(field)?.errors;
  }

  public getFormControl(controlName: string): FormControl {
    return this.formGroup.get(controlName) as FormControl;
  }

  updateBankDetails(bankName: string) {
    const selectedBank = this.bankOptions.find(
      (bank) => bank.name === bankName
    );
    this.minNum = selectedBank?.minNum ?? 10;
    this.maxNum = selectedBank?.maxNum ?? 12;
  }
}
